import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { RotateCcw, Tags, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import MasonryFeed from "../components/MasonryFeed";
import VerifiedDot from "../components/VerifiedDot";
import ShareMenu from "../components/ShareMenu";

export default function Profile() {
  const { id, slug } = useParams();
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [profile, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", bio: "", avatar_url: "" });
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const mine = user && user !== false && profile && user.id === profile.id;

  useEffect(() => {
    setProfile(null);
    setPhotos([]);
    setErr("");

    const fetchProfile = slug
      ? api.get(`/users/portfolio/${slug}`)
      : api.get(`/users/${id}`);

    fetchProfile
      .then(({ data }) => {
        setProfile(data);
        setForm({ name: data.name, bio: data.bio || "", avatar_url: data.avatar_url || "" });
        return api.get(`/photos`, { params: { user_id: data.id, limit: 200 } });
      })
      .then((res) => setPhotos(res?.data || []))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, [id, slug]);

  const save = async () => {
    try {
      const { data } = await api.patch("/users/me", form);
      setProfile((p) => ({ ...p, ...data }));
      if (mine) setUser(data);
      setEditing(false);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // Bulk-rescue for old photos with sideways thumbnails (pre-EXIF-fix uploads).
  // Reloads the photo list afterwards so the masonry shows the corrected sizes.
  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/photos/refresh-my-thumbnails");
      toast.success(
        t("refresh_all_done")
          .replace("{fixed}", data.fixed)
          .replace("{total}", data.total)
      );
      const fresh = await api.get(`/photos`, { params: { user_id: profile.id, limit: 200 } });
      setPhotos(fresh.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const toggleFollow = async () => {
    if (!user || user === false || mine || !profile) return;
    const isFollowing = profile.is_following;
    try {
      const { data } = isFollowing
        ? await api.delete(`/users/${profile.id}/follow`)
        : await api.post(`/users/${profile.id}/follow`);
      setProfile((p) => ({ ...p, is_following: data.is_following, followers: data.followers }));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const shareKey = profile && (profile.is_premium && profile.portfolio_slug ? profile.portfolio_slug : profile.id);

  // Keep document.title + og:* meta in sync so JS-aware crawlers and in-app
  // share sheets pick up the right portfolio.
  useEffect(() => {
    if (!profile) return;
    const prevTitle = document.title;
    const role = profile.is_premium ? "Portfolio member" : "Photographer";
    document.title = `${profile.name} — ${role} · Social Gallery`;
    const touched = [];
    const setMeta = (name, attr, value) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      const prior = el ? el.getAttribute("content") : null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
        touched.push({ el, prior: null, created: true });
      } else {
        touched.push({ el, prior, created: false });
      }
      el.setAttribute("content", value);
    };
    const key = profile.is_premium && profile.portfolio_slug ? profile.portfolio_slug : profile.id;
    const ogImg = `${process.env.REACT_APP_BACKEND_URL}/api/users/${key}/og.png`;
    const desc = (profile.bio ||
      `${role} on Social Gallery · ${profile.photos_count ?? 0} frames · ${profile.followers ?? 0} followers.`)
      .slice(0, 200);
    setMeta("description", "name", desc);
    setMeta("og:title", "property", `${profile.name} — ${role}`);
    setMeta("og:description", "property", desc);
    setMeta("og:image", "property", ogImg);
    setMeta("twitter:title", "name", `${profile.name} — ${role}`);
    setMeta("twitter:image", "name", ogImg);
    return () => {
      document.title = prevTitle;
      touched.forEach(({ el, prior, created }) => {
        if (created) el.remove();
        else if (prior !== null) el.setAttribute("content", prior);
      });
    };
  }, [profile]);

  if (err) return <div className="p-10 font-mono text-sm text-red-400">{err}</div>;
  if (!profile)
    return (
      <div className="p-10 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
        {t("loading")}
      </div>
    );

  return (
    <div className="mx-auto max-w-[1600px] px-6 sm:px-10 py-14" data-testid="profile-page">
      <section className="grid gap-10 lg:grid-cols-12 pb-12 border-b border-zinc-900">
        <div className="lg:col-span-4">
          {profile.avatar_url ? (
            <img
              src={profile.avatar_url}
              alt={profile.name}
              className="w-48 h-48 object-cover border border-zinc-800"
            />
          ) : (
            <div className="w-48 h-48 border border-zinc-800 flex items-center justify-center font-serif text-6xl text-zinc-500">
              {profile.name?.[0]?.toUpperCase()}
            </div>
          )}
        </div>
        <div className="lg:col-span-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500">
            {t("profile")}
          </div>
          {editing ? (
            <div className="mt-4 space-y-4 max-w-xl" data-testid="profile-edit-form">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border border-zinc-800 px-4 py-3 text-zinc-100 font-serif text-2xl focus:outline-none focus:border-zinc-500"
                data-testid="profile-name-input"
              />
              <textarea
                rows={4}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder={t("bio")}
                className="w-full bg-transparent border border-zinc-800 px-4 py-3 text-zinc-300 font-light focus:outline-none focus:border-zinc-500 resize-none"
                data-testid="profile-bio-input"
              />
              <input
                value={form.avatar_url}
                onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                placeholder={t("avatar_url")}
                className="w-full bg-transparent border border-zinc-800 px-4 py-3 text-zinc-300 font-light focus:outline-none focus:border-zinc-500"
                data-testid="profile-avatar-input"
              />
              <div className="flex gap-3">
                <button
                  onClick={save}
                  className="bg-sienna text-white px-5 py-2 text-xs uppercase tracking-[0.3em] hover:bg-sienna-400"
                  data-testid="profile-save-button"
                >
                  {t("save")}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="border border-zinc-800 text-zinc-300 px-5 py-2 text-xs uppercase tracking-[0.3em] hover:bg-zinc-900"
                  data-testid="profile-cancel-button"
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          ) : (
            <>
          <h1 className="mt-3 font-serif text-5xl sm:text-6xl text-zinc-50 font-light tracking-tighter leading-none flex items-center gap-3">
            {profile.name}
            <VerifiedDot visible={profile.is_premium} size={12} />
          </h1>
              {profile.bio && (
                <p className="mt-5 text-zinc-400 font-light leading-relaxed max-w-xl">
                  {profile.bio}
                </p>
              )}
              <div className="mt-6 flex flex-wrap gap-x-8 gap-y-4 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                <div>
                  <div className="text-zinc-100 text-xl font-serif font-light">{profile.photos_count ?? photos.length}</div>
                  <div>{t("frames")}</div>
                </div>
                <div>
                  <div className="text-zinc-100 text-xl font-serif font-light" data-testid="profile-followers-count">{profile.followers ?? 0}</div>
                  <div>{t("followers_label")}</div>
                </div>
                <div>
                  <div className="text-zinc-100 text-xl font-serif font-light">{profile.following ?? 0}</div>
                  <div>{t("following")}</div>
                </div>
                <div>
                  <div
                    className="text-zinc-100 text-xl font-serif font-light"
                    data-testid="profile-views-count"
                  >
                    {profile.total_views ?? 0}
                  </div>
                  <div>{t("stats_views")}</div>
                </div>
                <div>
                  <div
                    className="text-zinc-100 text-xl font-serif font-light"
                    data-testid="profile-shares-count"
                  >
                    {profile.total_shares ?? 0}
                  </div>
                  <div>{t("stats_shares")}</div>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                {mine ? (
                  <>
                    <button
                      onClick={() => setEditing(true)}
                      className="border border-zinc-800 text-zinc-200 px-5 py-2 text-xs uppercase tracking-[0.3em] hover:bg-zinc-900 hover:border-zinc-700 transition-colors"
                      data-testid="profile-edit-button"
                    >
                      {t("edit_profile")}
                    </button>
                    {photos.length > 0 && (
                      <>
                        <Link
                          to="/organize/genres"
                          className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-300 px-5 py-2 text-xs uppercase tracking-[0.3em] hover:bg-zinc-900 hover:border-zinc-700 transition-colors"
                          data-testid="profile-organize-genres-link"
                        >
                          <Tags size={12} /> {t("tools_organize")}
                        </Link>
                      </>
                    )}
                    {(photos.length > 0 || user?.role === "admin") && (
                      <>
                        <Link
                          to="/organize/duplicates"
                          className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-300 px-5 py-2 text-xs uppercase tracking-[0.3em] hover:bg-zinc-900 hover:border-zinc-700 transition-colors"
                          data-testid="profile-duplicates-link"
                        >
                          <Copy size={12} /> {t("tools_duplicates")}
                        </Link>
                      </>
                    )}
                    {photos.length > 0 && (
                      <button
                        onClick={handleRefreshAll}
                        disabled={refreshing}
                        className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-300 px-5 py-2 text-xs uppercase tracking-[0.3em] hover:bg-zinc-900 hover:border-zinc-700 disabled:opacity-50 transition-colors"
                        data-testid="profile-refresh-all-thumbs"
                      >
                        <RotateCcw size={12} className={refreshing ? "animate-spin" : ""} />
                        {refreshing ? t("refreshing_all") : t("refresh_all_thumbnails")}
                      </button>
                    )}
                  </>
                ) : user && user !== false ? (
                  <button
                    onClick={toggleFollow}
                    className={`px-5 py-2 text-xs uppercase tracking-[0.3em] transition-colors ${
                      profile.is_following
                        ? "border border-zinc-800 text-zinc-300 hover:bg-zinc-900"
                        : "bg-sienna text-white hover:bg-sienna-400"
                    }`}
                    data-testid="profile-follow-button"
                  >
                    {profile.is_following ? t("following") : t("follow")}
                  </button>
                ) : null}
                <ShareMenu
                  url={`${process.env.REACT_APP_BACKEND_URL}/api/users/${shareKey}/share`}
                  title={`${profile.name} · Social Gallery`}
                  testid="profile-share"
                />
              </div>
            </>
          )}
        </div>
      </section>

      <section className="pt-12">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-8">
          {t("works")}
        </div>
        {photos.length === 0 ? (
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
            {t("no_photos_yet")}
          </div>
        ) : (
          <MasonryFeed photos={photos} />
        )}
      </section>
    </div>
  );
}
